import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "./firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  serverTimestamp,
  increment,
  onSnapshot,
  getDoc,
  setDoc
} from "firebase/firestore";
import { storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import LiveChat from "./LiveChat";
import JournalSummariesTab from "./JournalSummariesTab";
import "./UserProfile.css";

const PACKAGE_VALIDITY_DAYS = {
  1: 1,  // 24 hours for 1 session
  2: 28, // 4 weeks
  4: 56  // 8 weeks
};

const ASSETS = {
  placeholders: {
    product: "/Nabi UI Assets/Assets_Images/Image_Video placeholder.png",
    service: "/Nabi UI Assets/Assets_Images/Image_Video placeholder.png",
  }
};

// Session storage helper functions
const saveUserSession = (userData, ordersData) => {
  try {
    const sessionData = {
      user: userData ? {
        uid: userData.uid,
        email: userData.email,
        displayName: userData.displayName
      } : null,
      orders: ordersData,
      lastUpdated: Date.now()
    };
    sessionStorage.setItem("userProfileData", JSON.stringify(sessionData));
    console.log("User session data saved successfully");
  } catch (err) {
    console.log("Error saving user session:", err);
  }
};

const getUserSession = () => {
  try {
    const sessionData = sessionStorage.getItem("userProfileData");
    return sessionData ? JSON.parse(sessionData) : null;
  } catch (err) {
    console.log("Error getting user session:", err);
    return null;
  }
};

const clearUserSession = () => {
  try {
    sessionStorage.removeItem("userProfileData");
    console.log("User session cleared");
  } catch (err) {
    console.log("Error clearing user session:", err);
  }
};
const isSessionSlotExpired = (dateStr, timeStr) => {
  if (!dateStr || !timeStr || dateStr === "TBD" || timeStr === "TBD") return false;

  try {
    // Parse the time (handles "2:00 PM" or "2:00 PM - 3:00 PM")
    let startTimeStr = timeStr;
    if (timeStr.includes(' - ')) {
      startTimeStr = timeStr.split(' - ')[0].trim();
    }

    const [time, modifier] = startTimeStr.split(' ');
    let [hours, minutes] = time.split(':');

    let hour24 = parseInt(hours);
    if (modifier && modifier.toUpperCase() === 'PM' && hour24 !== 12) hour24 += 12;
    if (modifier && modifier.toUpperCase() === 'AM' && hour24 === 12) hour24 = 0;

    const [year, month, day] = dateStr.split('-');
    const sessionDateTime = new Date(year, month - 1, day, hour24, parseInt(minutes || 0));

    return Date.now() > sessionDateTime.getTime();
  } catch (e) {
    console.error("Error checking session expiry:", e);
    return false;
  }
};

export default function UserProfile({ user, onContinueBrowsing }) {
  const [activeTab, setActiveTab] = useState("Therapy Sessions");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  const [schedulingItem, setSchedulingItem] = useState(null);
  const [sessionToChat, setSessionToChat] = useState(null); // New state for Active Live Chat
  const [activeChatSessionId, setActiveChatSessionId] = useState(null);
  const [activeChatTherapistName, setActiveChatTherapistName] = useState("");
  const [showQRId, setShowQRId] = useState(null);
  const [showActivity, setShowActivity] = useState(null); // New state for Activity Modal
  const [sessionActivitiesMap, setSessionActivitiesMap] = useState({}); // REAL-TIME MAP
  const [therapistImages, setTherapistImages] = useState({}); // Map of therapistId/Email to image URL
  const [expandedRefundId, setExpandedRefundId] = useState(null); // Click-based expansion for refund/cancel
  const [profileImage, setProfileImage] = useState(null);
  const [isStudentVerified, setIsStudentVerified] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingReview, setPendingReview] = useState(null);

  const [showEditModalItem, setShowEditModalItem] = useState(null);
  const [reschedulingSessionNum, setReschedulingSessionNum] = useState(null);

  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Event Reviews State
  const [attendedEvents, setAttendedEvents] = useState([]);
  const [userReviews, setUserReviews] = useState({});
  const [reviewingEvent, setReviewingEvent] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, text: "" });



  const storedUser = JSON.parse(sessionStorage.getItem("userData"));

  if (storedUser) {
    console.log(storedUser, "from sessionStorage");
  }

  const effectiveUser = user || storedUser;

  // Fetch user's profile image and verification status from Firestore
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!effectiveUser?.uid) return;
      try {
        const userRef = doc(db, "users", effectiveUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          setProfileImage(data.profileImage || null);
          setIsStudentVerified(data.isStudentVerified === true);
        } else {
          // If doc doesn't exist by UID, try searching by email (common in this app)
          const q = query(collection(db, "users"), where("email", "==", effectiveUser.email));
          const snap = await getDocs(q);
          if (!snap.empty) {
            const data = snap.docs[0].data();
            setProfileImage(data.profileImage || null);
            setIsStudentVerified(data.isStudentVerified === true);
          }
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };
    fetchUserProfile();
  }, [effectiveUser]);

  // Handle Profile Image Upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !effectiveUser?.uid) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `profile_images/${effectiveUser.uid}`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // Update Firestore
      const userRef = doc(db, "users", effectiveUser.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        await updateDoc(userRef, { profileImage: downloadURL });
      } else {
        // Find by email and update or create
        const q = query(collection(db, "users"), where("email", "==", effectiveUser.email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          await updateDoc(doc(db, "users", snap.docs[0].id), { profileImage: downloadURL, uid: effectiveUser.uid });
        } else {
          await setDoc(userRef, {
            uid: effectiveUser.uid,
            email: effectiveUser.email,
            name: effectiveUser.displayName || effectiveUser.name || "User",
            profileImage: downloadURL
          });
        }
      }

      setProfileImage(downloadURL);
      alert("✅ Profile image updated successfully!");
    } catch (err) {
      console.error("Upload failed:", err);
      alert("❌ Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Fetch all therapist images once to handle fallback for existing bookings
  useEffect(() => {
    const fetchTherapists = async () => {
      try {
        const snap = await getDocs(collection(db, "therapists"));
        const map = {};
        snap.forEach(doc => {
          const data = doc.data();
          if (data.image) {
            map[doc.id] = data.image;
            if (data.email) map[data.email] = data.image;
          }
        });
        setTherapistImages(map);
      } catch (err) {
        console.error("Error fetching therapist images:", err);
      }
    };
    fetchTherapists();
  }, []);

  // Listen for unread messages globally
  useEffect(() => {
    if (!effectiveUser?.uid) return;
    const q = query(collection(db, "chat_sessions"), where("userId", "==", effectiveUser.uid));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.unreadCountByPatient > 0) {
          count += data.unreadCountByPatient;
        } else if (data.unreadByPatient) {
          count += 1; // Fallback
        }
      });
      setTotalUnreadCount(count);
    });
    return () => unsubscribe();
  }, [effectiveUser]);

  // Fetch attended events and reviews
  useEffect(() => {
    if (!effectiveUser?.email) return;
    
    const fetchEventData = async () => {
      try {
        // Fetch events where attendees array contains user's email
        const qEvents = query(collection(db, "events"), where("attendees", "array-contains", effectiveUser.email));
        const eventSnaps = await getDocs(qEvents);
        const fetchedEvents = eventSnaps.docs.map(d => ({ ...d.data(), dbId: d.id }));
        setAttendedEvents(fetchedEvents);

        // Fetch existing reviews by this user
        const qReviews = query(collection(db, "event_reviews"), where("userEmail", "==", effectiveUser.email));
        const reviewSnaps = await getDocs(qReviews);
        const reviewsMap = {};
        reviewSnaps.forEach(d => {
            const data = d.data();
            reviewsMap[data.eventId] = data;
        });
        setUserReviews(reviewsMap);
      } catch (err) {
        console.error("Error fetching event reviews data:", err);
      }
    };
    fetchEventData();
  }, [effectiveUser]);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!effectiveUser || !effectiveUser.uid) {
        setLoading(false);
        return;
      }

      // Check for existing session data
      const savedSession = getUserSession();

      // If session exists and matches current user, use it
      if (savedSession && savedSession.user && savedSession.user.uid === effectiveUser.uid) {
        console.log("Using cached session data");
        setOrders(savedSession.orders);
        setLoading(false);
        return;
      }

      try {
        const uid = effectiveUser.uid;
        const email = effectiveUser.email; // Used to reliably find bookings
        const allItems = [];

        // 1. Fetch Therapy Bookings
        const bookingsRef = collection(db, "bookings");

        // Removed the Firebase orderBy() to prevent Index crash errors.
        // We search by email first (as TherapistPage often saves using email), 
        // and if it fails, we fall back to userId.
        let qBookings = query(bookingsRef, where("email", "==", email));
        let bookingSnaps = await getDocs(qBookings);
        // console.log(bookingSnaps, "bookingSnapsbookingSnaps")

        if (bookingSnaps.empty) {
          qBookings = query(bookingsRef, where("userId", "==", uid));
          bookingSnaps = await getDocs(qBookings);
        }

        // console.log(bookingSnaps, "kjhgfdfguijhkingSnaps")
        console.log(bookingSnaps.docs);
        console.log(bookingSnaps.size);
        console.log(bookingSnaps.empty);

        bookingSnaps.forEach(doc => {
          console.log(doc, "came here");
          // console.log(doc.id, doc.data(), "console inside the bookingsnaps");
          const data = doc.data();
          const sessionCount = parseInt(data.sessionCount) || parseInt(data.totalSessions) || 1;

          // Recalculate display value for UI purposes
          let calculatedSessionsUsed = 0;
          if ((data.date && data.date !== "TBD") || (data.session_1 && data.session_1.date && data.session_1.date !== "TBD")) {
            calculatedSessionsUsed = 1;
          }
          for (let i = 2; i <= sessionCount; i++) {
            const sKey = `session_${i}`;
            if (data[sKey] && data[sKey].date && data[sKey].date !== "TBD") {
              calculatedSessionsUsed = i;
            }
          }

          // Use the actual database value for sessionsUsed to ensure cancellation logic works correctly
          const sessionsUsed = data.sessionsUsed || 0;
          const displaySessionsUsed = calculatedSessionsUsed;

          const bookingItem = {
            id: doc.id,
            type: "service",
            name: "Therapy Session",
            therapist: data.therapistName || "Assigned Therapist",
            userName: data.userName || data.fullName || effectiveUser?.name || effectiveUser?.displayName || "Customer",
            therapistId: data.therapistId,
            therapistEmail: data.therapistEmail,
            location: "Online",
            date: data.date || "TBD",
            time: data.timeSlot || "TBD",
            duration: "60 mins",
            price: parseFloat(data.price) || 0,
            image: data.therapistImage || ASSETS.placeholders.service,
            sessionCount: sessionCount,
            sessionsUsed: sessionsUsed,
            displaySessionsUsed: displaySessionsUsed,
            remainingSessions: Math.max(0, sessionCount - sessionsUsed),
            isTaken: data.isTaken || false,
            status: data.status || "confirmed",
            createdAt: data.createdAt ? data.createdAt.toMillis() : Date.now(),
            meetLink: data.meetLink || null,
            paymentId: data.paymentId || null,
            paymentStatus: data.paymentStatus || null,
            paymentStatusMessage: data.paymentStatusMessage || null
          };

          for (let i = 1; i <= sessionCount; i++) {
            if (data[`session_${i}`]) {
              bookingItem[`session_${i}`] = data[`session_${i}`];
            }
          }

          allItems.push(bookingItem);
        });

        // 2. Fetch Store Orders
        const ordersRef = collection(db, "orders");
        // Removed the Firebase orderBy() here too
        const qOrders = query(ordersRef, where("userId", "==", uid));
        const orderSnaps = await getDocs(qOrders);

        orderSnaps.forEach(doc => {
          const data = doc.data();
          if (data.items && Array.isArray(data.items)) {
            data.items.forEach(item => {
              allItems.push({
                id: `${doc.id}_${item.id}`,
                type: "product",
                name: item.name,
                price: parseFloat(item.price) || 0,
                qty: item.qty || 1,
                options: ["Standard"],
                status: data.shipmentStatus || "Processing",
                image: item.image || ASSETS.placeholders.product,
                createdAt: data.createdAt ? data.createdAt.toMillis() : Date.now()
              });
            });
          }
        });

        // group items
        const parentBookings = allItems.filter(item => item.type === "product" || !item.parentBookingId);
        const childBookings = allItems.filter(item => item.type === "service" && item.parentBookingId);

        parentBookings.forEach(parent => {
          if (parent.type === "service") {
            parent.children = childBookings.filter(child => child.parentBookingId === parent.id);
            parent.children.sort((a, b) => a.createdAt - b.createdAt);
          }
        });

        // 3. Sort Combined List by Date (Newest First) using JavaScript instead!
        parentBookings.sort((a, b) => b.createdAt - a.createdAt);

        setOrders(parentBookings);

        // Save to session storage if user is logged in
        if (user && user.uid) {
          saveUserSession(user, parentBookings);
        }

      } catch (error) {
        console.error("Error fetching history:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [user]);

  // --- NEW: DETECT PENDING REVIEWS ---
  useEffect(() => {
    if (loading || !orders.length) return;

    // Check for sessions that are completed but not reviewed yet
    for (const order of orders) {
      if (order.type !== 'service' || order.status === 'cancelled') continue;

      for (let i = 1; i <= (order.sessionCount || 1); i++) {
        const sessionKey = `session_${i}`;
        const session = order[sessionKey];

        // If it's session 1 and not explicitly in session_1 object, it might be in root
        const isSessionTaken = session ? (session.isTaken || session.isCompleted) : (i === 1 && (order.isTaken || order.isCompleted));
        const isReviewed = session ? session.reviewSubmitted : (i === 1 && order.reviewSubmitted);

        if (isSessionTaken && !isReviewed) {
          // Trigger review modal
          setPendingReview({ booking: order, sessionNumber: i });
          return; // Show one review at a time
        }
      }
    }
  }, [orders, loading]);

  // --- NEW: FETCH ACTIVITIES (Real-time for Patient) ---
  useEffect(() => {
    if (!effectiveUser?.uid) return;

    const activitiesRef = collection(db, "session_activities");
    const q = query(
      activitiesRef,
      where("patientId", "==", effectiveUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const activitiesMap = {};
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.bookingId && data.sessionNumber) {
          activitiesMap[`${data.bookingId}_${data.sessionNumber}`] = data.activities || [];
        }
      });
      setSessionActivitiesMap(activitiesMap);
    });

    return () => unsubscribe();
  }, [effectiveUser?.uid]);

  // helper function for cancelling and refund 
  // ADD THESE HELPER FUNCTIONS FOR CANCELLATION WINDOW
  const getCancellationDeadline = (createdAt) => {
    if (!createdAt) return null;
    const deadline = new Date(createdAt);
    deadline.setHours(deadline.getHours() + 6);
    return deadline;
  };

  const isWithinCancellationWindow = (createdAt) => {
    if (!createdAt) return false;
    const now = Date.now();
    const sixHoursInMs = 6 * 60 * 60 * 1000;
    return (now - createdAt) <= sixHoursInMs;
  };

  const getTimeRemainingForCancellation = (createdAt) => {
    if (!createdAt) return 0;
    const now = Date.now();
    const sixHoursInMs = 6 * 60 * 60 * 1000;
    const elapsed = now - createdAt;
    const remaining = sixHoursInMs - elapsed;
    return Math.max(0, remaining);
  };

  const formatTimeRemaining = (milliseconds) => {
    if (milliseconds <= 0) return "Expired";
    const hours = Math.floor(milliseconds / (3600000));
    const minutes = Math.floor((milliseconds % 3600000) / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);

    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} min`;
    }
    if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`;
  };

  // Helper for Google Calendar Link
  const addToCalendarUrl = (item) => {
    const text = encodeURIComponent(`Therapy with ${item.therapist}`);
    const details = encodeURIComponent(`Session with ${item.therapist}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&details=${details}`;
  };

  const calculateTotal = () => {
    return orders.reduce((acc, item) => acc + (item.price * (item.qty || 1)), 0);
  };

  const calculateTherapyTotal = () => {
    return orders.filter(item => item.type === 'service').reduce((acc, item) => acc + item.price, 0);
  };

  const calculateStoreTotal = () => {
    return orders.filter(item => item.type === 'product').reduce((acc, item) => acc + (item.price * (item.qty || 1)), 0);
  };

  const isSessionExpired = (dateStr, timeStr) => {
    if (!dateStr || !timeStr || dateStr === "TBD" || timeStr === "TBD") return false;

    try {
      const timeParts = timeStr.trim().split('-');
      const targetTime = timeParts[timeParts.length - 1].trim();
      let sessionDate = new Date(`${dateStr} ${targetTime}`);

      if (isNaN(sessionDate.getTime())) {
        // Fallback for YYYY-MM-DD parsing
        const parts = dateStr.split('-');
        let year, month, day;
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            year = parts[0]; month = parts[1]; day = parts[2];
          } else {
            day = parts[0]; month = parts[1]; year = parts[2];
          }
        }

        // Use the end time of the range if available (e.g., "10:00 - 11:00 AM" -> "11:00 AM")
        const timeParts = timeStr.trim().split('-');
        const targetTime = timeParts[timeParts.length - 1].trim();

        let [time, modifier] = targetTime.split(' ');

        // If the end time doesn't have a modifier but the start time did (e.g., "10:00 AM - 11:00")
        if (!modifier && timeParts.length > 1) {
          const firstPart = timeParts[0].trim();
          if (firstPart.toUpperCase().includes('AM')) modifier = 'AM';
          else if (firstPart.toUpperCase().includes('PM')) modifier = 'PM';
        }

        let [hours, minutes] = time.split(':');
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (modifier && modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
        if (modifier && modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;

        sessionDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hours, minutes);
      }

      if (isNaN(sessionDate.getTime())) return false;

      // Expired if current time is past the session start time
      return Date.now() > sessionDate.getTime();
    } catch (e) {
      return false;
    }
  };

  const isPackageExpired = (createdAtMillis, sessionCount, item) => {
    if (!item) return false;

    const totalSessions = parseInt(sessionCount) || 1;
    let lastSessionData = item[`session_${totalSessions}`];
    if (!lastSessionData && totalSessions === 1) lastSessionData = item; // Fallback for 1-session

    const validityDays = PACKAGE_VALIDITY_DAYS[totalSessions] || PACKAGE_VALIDITY_DAYS[1];

    // If the last session is not even booked yet, chat expires at the package validity deadline.
    if (!lastSessionData || !lastSessionData.date || lastSessionData.date === "TBD") {
      if (!createdAtMillis) return false;
      const createDate = new Date(createdAtMillis).getTime();
      const expirationTime = createDate + validityDays * 24 * 60 * 60 * 1000;
      return Date.now() > expirationTime;
    }

    // If the last session is booked, calculate 24 hours after its end time
    let expirationTime = 0;
    try {
        const timeParts = (lastSessionData.time || "").trim().split('-');
        const targetTime = timeParts[timeParts.length - 1]?.trim() || "";
        
        const dateParts = lastSessionData.date.split('-');
        let year, month, day;
        if (dateParts.length === 3) {
            if (dateParts[0].length === 4) {
            year = dateParts[0]; month = dateParts[1]; day = dateParts[2];
            } else {
            day = dateParts[0]; month = dateParts[1]; year = dateParts[2];
            }
            
            let [timeStr, modifier] = targetTime.split(' ');
            if (!modifier && timeParts.length > 1) {
            const firstPart = timeParts[0].trim();
            if (firstPart.toUpperCase().includes('AM')) modifier = 'AM';
            else if (firstPart.toUpperCase().includes('PM')) modifier = 'PM';
            }
            
            let [hours, minutes] = (timeStr || "0:0").split(':');
            hours = parseInt(hours, 10);
            minutes = parseInt(minutes, 10);
            if (modifier && modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (modifier && modifier.toUpperCase() === 'AM' && hours === 12) hours = 0;
            
            const sessionDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hours, minutes);
            if (!isNaN(sessionDate.getTime())) {
                expirationTime = sessionDate.getTime() + 24 * 60 * 60 * 1000;
            }
        }
    } catch (e) {
        console.error("Error parsing session date in isPackageExpired:", e);
    }

    if (expirationTime > 0) {
        return Date.now() > expirationTime;
    }

    // Fallback if parsing fails
    if (createdAtMillis) {
       const createDate = new Date(createdAtMillis).getTime();
       return Date.now() > (createDate + validityDays * 24 * 60 * 60 * 1000);
    }

    return false;
  };

  const filteredOrders = orders.filter(item => {
    // Add default behavior for our custom tabs
    if (activeTab === "Journal Summaries" || activeTab === "Event Reviews") {
      return false;
    }

    if (activeTab === "Therapy Sessions") {
      return item.type === 'service';
    }
    if (activeTab === "Store Orders") {
      return item.type === 'product';
    }
    if (activeTab === "Journal Summaries") {
      return false;
    }
    return true;
  });

  // const handleCancelBooking = async (item) => {
  //   if (!window.confirm(`Are you sure you want to cancel your ${item.name} with ${item.therapist}? A full refund of ₹${item.price} will be initiated.`)) {
  //     return;
  //   }

  //   setCancellingId(item.id);
  //   try {
  //     const response = await fetch("https://cancelbooking-qxb2xs4n7a-uc.a.run.app", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         bookingId: item.id,
  //         userId: user?.uid || "guest"
  //       })
  //     });

  //     const result = await response.json();
  //     if (response.ok) {
  //       alert("Booking cancelled and refund initiated successfully!");
  //       window.location.reload();
  //     } else {
  //       alert(`Cancellation failed: ${result.error || "Unknown error"}`);
  //     }
  //   } catch (err) {
  //     console.error("Cancellation error:", err);
  //     alert("Failed to connect to the server. Please check your connection.");
  //   } finally {
  //     setCancellingId(null);
  //   }
  // };


  // REPLACE THE ENTIRE handleCancelBooking FUNCTION WITH THIS
  const handleCancelBooking = async (item) => {
    // Check if within cancellation window first
    if (!isWithinCancellationWindow(item.createdAt)) {
      alert("Cancellation window has expired. Refunds are only available within 6 hours of booking.");
      return;
    }

    const timeRemaining = getTimeRemainingForCancellation(item.createdAt);

    if (!window.confirm(
      `⚠️ Confirm Cancellation\n\n` +
      `Are you sure you want to cancel your ${item.name} with ${item.therapist}?\n\n` +
      `✅ Full refund of ₹${item.price} will be initiated\n` +
      `⏰ You have ${formatTimeRemaining(timeRemaining)} remaining in your cancellation window\n\n` +
      `This action cannot be undone.`
    )) {
      return;
    }

    setCancellingId(item.id);
    try {
      console.log("Initiating secure refund via Cloud Function for booking:", item.id);

      // --- 1. CALL SECURE CLOUD FUNCTION ---
      // This bypasses CORS because the refund happens on the server.
      const response = await fetch("https://cancelbooking-qxb2xs4n7a-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bookingId: item.id,
          userId: effectiveUser?.uid || "guest",
          userEmail: effectiveUser?.email
        })
      });

      const result = await response.json();

      if (response.ok) {
        // --- 2. AUDIT LOGGING (FRONTEND) ---
        try {
          await addDoc(collection(db, "payment_audit"), {
            bookingId: item.id,
            userId: effectiveUser?.uid || "guest",
            userEmail: effectiveUser?.email,
            action: "REFUND_VIA_CLOUD_FUNCTION",
            amount: item.price,
            refundId: result.refundId || "unknown",
            status: result.refundStatus === "success" ? "success" : "manual_followup_needed",
            error: result.refundError || null,
            timestamp: serverTimestamp()
          });
          console.log("✅ Audit log created");
        } catch (auditErr) {
          console.error("❌ Audit logging failed:", auditErr);
        }

        // --- 3. USER FEEDBACK ---
        if (result.refundStatus === "failed") {
          alert(`✅ Booking cancelled successfully.\n\n⚠️ NOTE: Automatic refund initiation encountered an issue: "${result.refundError || "Unknown Razorpay error"}".\n\nPlease contact support at reachout@nabi.health with your Payment ID to process the refund manually.`);
        } else {
          alert(`✅ Booking cancelled successfully!\n\nRefund of ₹${result.refundAmount || item.price} has been initiated.\nIt will reflect in your account within 5-7 business days.`);
        }

        clearUserSession();
        window.location.reload();
      } else {
        const errorMsg = result.error || "Unknown error";
        console.error("Cancellation failed:", errorMsg, result);
        alert(`❌ Cancellation failed: ${errorMsg}`);
      }

    } catch (err) {
      console.error("Refund Initiation Failed:", err);
      alert(`❌ Failed to connect to the server: ${err.message}. Please check your connection and try again.`);
    } finally {
      setCancellingId(null);
    }
  };

  const handleRenew = () => {
    if (onContinueBrowsing) {
      onContinueBrowsing();
    } else {
      window.location.href = '/therapist';
    }
  };

  const handleInitiateChat = async (item) => {
    // Standardized sessionId format: chat_{userId}_{bookingId}
    const sessionId = `chat_${effectiveUser.uid}_${item.id}`;

    try {
      // Ensure the session document exists with current booking context
      await setDoc(doc(db, "chat_sessions", sessionId), {
        userId: effectiveUser.uid,
        userName: effectiveUser.displayName || effectiveUser.name || "User",
        therapistId: item.therapistId,
        therapistName: item.therapist,
        status: "active",
        updatedAt: serverTimestamp(),
        // NEW: Comprehensive booking context
        bookingId: item.id,
        packageType: item.name || "Therapy Package",
        totalSessions: item.sessionCount || 1,
        sessionsUsed: item.bookingsUsed || item.sessionsUsed || 0,
        lastBookingDate: item.createdAt || null
      }, { merge: true });

      setActiveChatSessionId(sessionId);
      setActiveChatTherapistName(item.therapist);
    } catch (err) {
      console.error("Error initiating chat:", err);
      setActiveChatSessionId(sessionId);
    }
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      const { rating, feedback, sessionNumber, bookingId } = reviewData;
      if (rating === 0) {
        alert("Please select a rating! 😊");
        return;
      }

      // 1. Save to session_reviews collection
      await addDoc(collection(db, "session_reviews"), {
        bookingId,
        sessionNumber,
        userId: effectiveUser.uid,
        userName: effectiveUser.displayName || effectiveUser.name || "User",
        therapistId: pendingReview.booking.therapistId || "",
        therapistEmail: pendingReview.booking.therapistEmail || "",
        therapistName: pendingReview.booking.therapist || "",
        userType: pendingReview.booking.isStudent ? "Student" : "",
        rating,
        feedback,
        timestamp: serverTimestamp()
      });

      // 2. Update the booking document to mark session as reviewed
      const bookingRef = doc(db, "bookings", bookingId);
      const sessionKey = `session_${sessionNumber}`;

      // Update specifically based on where the session data is (root for session 1 fallback or specific key)
      const updatePayload = {
        [`${sessionKey}.reviewSubmitted`]: true,
        [`${sessionKey}.reviewRating`]: rating,
        [`${sessionKey}.reviewFeedback`]: feedback
      };

      if (sessionNumber === 1) {
        updatePayload.reviewSubmitted = true;
        updatePayload.reviewRating = rating;
        updatePayload.reviewFeedback = feedback;
      }

      await updateDoc(bookingRef, updatePayload);

      // 3. Clear pending review and update local state to prevent re-triggering
      setPendingReview(null);

      setOrders(prevOrders => prevOrders.map(order => {
        if (order.id === bookingId) {
          const updatedOrder = { ...order };
          if (sessionNumber === 1) {
            updatedOrder.reviewSubmitted = true;
            updatedOrder.reviewRating = rating;
            updatedOrder.reviewFeedback = feedback;
          }
          const sessionKey = `session_${sessionNumber}`;
          if (updatedOrder[sessionKey]) {
            updatedOrder[sessionKey] = {
              ...updatedOrder[sessionKey],
              reviewSubmitted: true,
              reviewRating: rating,
              reviewFeedback: feedback
            };
          } else if (sessionNumber === 1) {
            // If session_1 doesn't exist as an object, it's already handled by root fields
          }
          return updatedOrder;
        }
        return order;
      }));

      alert("Thank you for your feedback! It helps us serve you better.");
    } catch (err) {
      console.error("Error submitting review:", err);
      alert("Failed to submit review. Please try again.");
    }
  };

  return (
    <div className="user-profile-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="user-profile-container"
      >
        <div className="user-profile-header">
          <div className="user-profile-info-section">
            <div className="user-profile-avatar-container">
              <div className="user-profile-avatar">
                {profileImage ? (
                  <img src={profileImage} alt="Profile" />
                ) : (
                  <div className="user-profile-avatar-placeholder">
                    {effectiveUser?.name?.charAt(0) || effectiveUser?.displayName?.charAt(0) || "U"}
                  </div>
                )}
                <label className="user-profile-avatar-edit">
                  <input type="file" onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} />
                  <span>{uploading ? "..." : "✎"}</span>
                </label>
              </div>
            </div>
            <div className="user-profile-text-section">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h1 className="user-profile-title">You're all set, {effectiveUser?.name?.split(' ')[0] || effectiveUser?.displayName?.split(' ')[0] || "Customer"}!</h1>
                <div style={{
                  position: 'relative',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{ fontSize: '24px' }}>🔔</span>
                  {totalUnreadCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-8px',
                      backgroundColor: '#F07167',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold'
                    }}>
                      {totalUnreadCount}
                    </span>
                  )}
                </div>
                {isStudentVerified && (
                  <div style={{
                    background: '#C6F6D5',
                    color: '#22543D',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <img src="/Nabi UI Assets/Assets_Icon/Icon_Check circle filled.svg" alt="verified" style={{ width: '12px' }} />
                    STUDENT VERIFIED
                  </div>
                )}

              </div>
              <p className="user-profile-subtitle">
                Here is a history of your purchases and scheduled sessions.
              </p>
            </div>
          </div>
        </div>

        <div className="user-profile-content-layout">

          {/* SIDE NAVIGATION PANEL */}
          <div className="user-profile-sidebar">
            {["Therapy Sessions", "Store Orders", "Journal Summaries", "Event Reviews"].map(tab => (
            
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`user-profile-tab-btn ${activeTab === tab ? 'active' : ''}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* LIST CONTAINER */}
          <div className="user-profile-main-list">
            <div className="user-profile-list-container">
            {activeTab === "Journal Summaries" && (
                <JournalSummariesTab user={effectiveUser} />
              )}

              {activeTab === "Event Reviews" && (
                <div className="user-profile-reviews-container">
                    <h2 style={{ fontSize: '18px', color: '#003049', marginBottom: '15px' }}>My Attended Events</h2>
                    {attendedEvents.length === 0 ? (
                        <p style={{ color: '#666' }}>You have not attended any events yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            {attendedEvents.map(ev => {
                                const existingReview = userReviews[ev.dbId];
                                return (
                                    <div key={ev.dbId} style={{ background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <h4 style={{ margin: '0 0 5px 0', color: '#003049' }}>{ev.title}</h4>
                                            <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{ev.date} - {ev.location}</p>
                                        </div>
                                        <div>
                                            {existingReview ? (
                                                <span style={{ fontSize: '13px', padding: '6px 12px', borderRadius: '20px', background: existingReview.status === 'approved' ? '#eefbf3' : existingReview.status === 'rejected' ? '#faeced' : '#fff9e6', color: existingReview.status === 'approved' ? '#155724' : existingReview.status === 'rejected' ? '#721c24' : '#856404' }}>
                                                    {existingReview.status === 'approved' ? 'Review Approved' : existingReview.status === 'rejected' ? 'Review Rejected' : 'Review Pending'}
                                                </span>
                                            ) : (
                                                <button onClick={() => setReviewingEvent(ev)} style={{ padding: '8px 16px', background: '#f29053', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                                                    Leave a Review
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
              )}

               {loading && activeTab !== "Journal Summaries" && activeTab !== "Event Reviews" && <p style={{ textAlign: 'center', padding: '20px' }}>Loading history...</p>} 

               {!loading && filteredOrders.length === 0 && activeTab !== "Journal Summaries" && activeTab !== "Event Reviews" && (
                <p style={{ textAlign: 'center', padding: '40px', color: '#777' }}>No {activeTab.toLowerCase()} found.</p>
              )}

              {!loading && filteredOrders.map((item, index) => (
                <div key={index} className="user-profile-card">
                  {/* PRODUCT CARD UI */}
                  {item.type === 'product' && (
                    <div className="user-profile-card-content">
                      <div className="user-profile-image-box">
                        <img src={item.image} alt={item.name} className="user-profile-img" />
                      </div>
                      <div className="user-profile-info-box">
                        <div className="user-profile-info-top">
                          <h3 className="user-profile-item-name">{item.name}</h3>
                          <span className="user-profile-qty">Qty: {item.qty}</span>
                        </div>
                        <p className="user-profile-price">₹{item.price?.toFixed(2)}</p>
                        <p style={{ fontSize: '0.8rem', color: item.status === 'Processing' ? 'orange' : 'green' }}>{item.status}</p>
                      </div>
                    </div>
                  )}

                  {/* SERVICE/THERAPIST CARD UI */}
                  {item.type === 'service' && (
                    <div className="user-profile-card-content">
                      <div className="user-profile-image-box">
                        {((item.therapistId && therapistImages[item.therapistId]) || (item.therapistEmail && therapistImages[item.therapistEmail]) || (item.image && item.image !== ASSETS.placeholders.service ? item.image : null)) ? (
                          <img
                            src={(item.therapistId && therapistImages[item.therapistId]) || (item.therapistEmail && therapistImages[item.therapistEmail]) || item.image}
                            alt={item.therapist || "Therapist"}
                            className="user-profile-img"
                          />
                        ) : (
                          <div className="user-profile-img-placeholder">
                            {item.therapist?.charAt(0) || "T"}
                          </div>
                        )}
                      </div>
                      <div className="user-profile-info-box">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h3 className="user-profile-item-name" style={{ margin: 0 }}>{item.name}</h3>
                            {(item.appliedPromo === 'TRUSTFIRST' || Number(item.price) === 0) && (
                              <span style={{ backgroundColor: '#F07167', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                                TRUSTFIRST
                              </span>
                            )}
                          </div>
                          {!(item.appliedPromo === 'TRUSTFIRST' || Number(item.price) === 0) && (
                            <button
                              onClick={() => setShowEditModalItem(item)}
                              className="edit-session-btn"
                              style={{
                                padding: '6px 14px',
                                borderRadius: '20px',
                                border: '1px solid #003049',
                                backgroundColor: 'transparent',
                                color: '#003049',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontFamily: "'Syne', sans-serif",
                                transition: 'all 0.2s'
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        <p className="user-profile-price" style={{ marginTop: '4px', marginBottom: '8px' }}>₹{item.price?.toFixed(2)}</p>

                        {item.takenMessage}

                        <div className="user-profile-service-details" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'end',
                          marginTop: '5px'
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <p className="user-profile-detail-text" style={{ margin: 0 }}>
                              <strong>Package Paid On:</strong> {new Date(item.createdAt).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                            </p>
                            <p className="user-profile-detail-text" style={{ margin: 0 }}>
                              <strong>Booked For:</strong> {item.userName}
                            </p>
                            <p className="user-profile-detail-text" style={{ margin: 0 }}>{item.duration}</p>
                            <p className="user-profile-detail-text" style={{ margin: 0 }}>{item.therapist}</p>
                            <p className="user-profile-detail-text" style={{ margin: 0 }}>{item.location}</p>
                          </div>
                          <div>
                              <button
                                className="activity-btn"
                                onClick={() => setShowActivity({ booking: item, sessionNumber: 1 })}
                                style={{
                                  margin: 0,
                                  padding: '5px 12px',
                                  fontSize: '11px',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                📑 Activity
                              </button>
                          </div>
                        </div>

                        {/* Chat Section & Cancelled Status Box */}
                        <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          <div className="chat-section">
                            {(item.appliedPromo === 'TRUSTFIRST' || Number(item.price) === 0) ? (
                              <button
                                onClick={() => setShowQRId(item.id)}
                                style={{ ...styles.chatInlineBtn, backgroundColor: '#F07167', color: '#FFF', padding: '10px 20px', fontSize: '14px', marginTop: 0 }}
                              >
                                Queries? Scan QR
                              </button>
                            ) : !isPackageExpired(item.createdAt, item.sessionCount, item) ? (
                              <button
                                onClick={() => handleInitiateChat(item)}
                                style={{ ...styles.chatInlineBtn, padding: '10px 20px', fontSize: '14px', marginTop: 0 }}
                              >
                                💬 Chat with {item.therapist}
                              </button>
                            ) : (
                              <div style={{ padding: '10px', backgroundColor: '#FFF5F5', borderRadius: '8px', borderLeft: '4px solid #D62828' }}>
                                <p className="user-profile-detail-text" style={{ color: '#D62828', fontWeight: 'bold', margin: 0, fontSize: '12px' }}>
                                  ⚠️ Chat Support Expired
                                </p>
                                <button
                                  onClick={() => setShowQRId(item.id)}
                                  style={{ ...styles.chatInlineBtn, backgroundColor: '#F07167', color: '#FFF', padding: '6px 12px', fontSize: '11px', marginTop: '5px' }}
                                >
                                  Queries? Scan QR
                                </button>
                              </div>
                            )}
                          </div>

                          {item.status === "cancelled" && (
                            <div style={{ padding: '12px', backgroundColor: '#F5F5F5', borderRadius: '8px', borderLeft: '4px solid #666', marginTop: '5px' }}>
                              {item.paymentStatus === "refunded" ? (
                                <p className="user-profile-detail-text" style={{ color: '#2A9D8F', fontWeight: 'bold', margin: 0 }}>
                                  🚫 Cancelled & Refunded
                                </p>
                              ) : (
                                <p className="user-profile-detail-text" style={{ color: '#F07167', fontWeight: 'bold', margin: 0 }}>
                                  🚫 Refund Pending Admin Review
                                </p>
                              )}
                              
                              {item.paymentStatus === "refunded" ? (
                                <p style={{ marginTop: '8px', fontSize: '13px', color: '#003049', fontWeight: '500', margin: 0 }}>
                                  ✅ Refund initiated successfully. You will receive your money in 6-7 business days.
                                </p>
                              ) : (
                                <p style={{ marginTop: '8px', fontSize: '13px', color: '#003049', fontWeight: '500', margin: 0 }}>
                                  ✅ Admin will check and initiate refund, no worries. You will receive an email once it is processed.
                                </p>
                              )}
                            </div>
                          )}
                        </div>





                        {/* PACKAGE SESSION BOXES */}
                        <div style={{ marginTop: '15px' }}>
                          <div className="user-profile-session-grid">
                            {Array.from({ length: item.sessionCount }).map((_, i) => {
                              const sessionNum = i + 1;
                              const isActive = sessionNum === item.displaySessionsUsed + 1;

                              // Extract actual session details natively stored on booking payload
                              let sessionData = null;
                              if (item[`session_${sessionNum}`]) {
                                sessionData = item[`session_${sessionNum}`];
                              } else if (sessionNum === 1) {
                                sessionData = item;
                              }

                              const hasData = !!sessionData && sessionData.date && sessionData.date !== "TBD";
                              const slotExpired = hasData ? isSessionExpired(sessionData.date, sessionData.time) : false;
                              const isTaken = hasData ? (sessionData.isTaken || sessionData.isCompleted) : false;
                              const isUsed = hasData;

                              const getOrdinal = (n) => {
                                const s = ["th", "st", "nd", "rd"];
                                const v = n % 100;
                                return n + (s[(v - 20) % 10] || s[v] || s[0]);
                              };

                              const formatDate = (dateStr) => {
                                if (!dateStr || dateStr === "TBD") return dateStr;
                                const parts = dateStr.split('-');
                                if (parts.length !== 3) return dateStr;
                                const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                                if (isNaN(d.getTime())) return dateStr;
                                const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                                return `${getOrdinal(d.getDate())} ${months[d.getMonth()]}`;
                              };

                              return (
                                <div key={i} className="user-profile-session-column">
                                  <div className="user-profile-session-header">
                                    {getOrdinal(sessionNum)} session
                                  </div>
                                  <div className="user-profile-session-body">

                                    {hasData && sessionData.date !== "TBD" && (
                                      <div style={{ marginBottom: '8px' }}>
                                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#000', fontWeight: 'bold' }}>{formatDate(sessionData.date)}</p>
                                        <p style={{ margin: 0, fontSize: '0.85rem', color: '#555' }}>{sessionData.time}</p>
                                      </div>
                                    )}

                                    {isUsed && isTaken && (
                                      <span style={{ fontSize: '0.85rem', color: '#2A9D8F', fontWeight: 'bold' }}>session completed</span>
                                    )}

                                    {isUsed && !isTaken && slotExpired && (
                                      <span style={{ fontSize: '0.85rem', color: item.status === "cancelled" ? "#666" : "#D62828", fontWeight: 'bold' }}>
                                        {item.status === "cancelled" ? "cancelled" : "expired: you have not taken session"}
                                      </span>
                                    )}

                                    {isUsed && !isTaken && !slotExpired && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontSize: '0.85rem', color: item.status === "cancelled" ? "#666" : "#F07167", fontWeight: 'bold' }}>
                                          {item.status === "cancelled" ? "cancelled" : "upcoming"}
                                        </span>
                                        {sessionData.meetLink && (
                                          <a
                                            href={sessionData.meetLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="user-profile-schedule-btn"
                                            style={{
                                              textDecoration: 'none',
                                              textAlign: 'center',
                                              backgroundColor: '#2A9D8F'
                                            }}
                                          >
                                            Join Session
                                          </a>
                                        )}
                                      </div>
                                    )}

                                    {isActive && !hasData && (
                                      <button
                                        onClick={() => setSchedulingItem(item)}
                                        className="user-profile-schedule-btn"
                                      >
                                        schedule session / book session
                                      </button>
                                    )}

                                    {!isUsed && !isActive && (
                                      <span style={{ fontSize: '0.85rem', color: '#555' }}>
                                        after {getOrdinal(sessionNum - 1)} session
                                      </span>
                                    )}

                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* ORDER SUMMARY (Visible only if there are orders) */}
              {activeTab !== "Journal Summaries" && activeTab !== "Event Reviews" && orders.length > 0 && (
                <div className="user-profile-summary-section">
                  <div className="user-profile-note-section">
                    <p className="user-profile-label" style={{ fontWeight: '600' }}>Note</p>
                    <p className="user-profile-note-text">Thank you for being part of Nabi.</p>
                  </div>
                  <div className="user-profile-totals-section">
                    {activeTab === "Therapy Sessions" && (
                      <div className="user-profile-summary-row"><span>Therapy Sessions Total</span><span>₹{calculateTherapyTotal().toFixed(2)}</span></div>
                    )}
                    {activeTab === "Store Orders" && (
                      <div className="user-profile-summary-row"><span>Store Orders Total</span><span>₹{calculateStoreTotal().toFixed(2)}</span></div>
                    )}
                    <div className="user-profile-summary-row"><span>Delivery</span><span>Free</span></div>
                    <div className="user-profile-summary-row"><span>Taxes</span><span>₹0.00</span></div>
                    <div className="user-profile-total-row">
                      <span>Total Spent:</span>
                      <span>₹{(activeTab === "Therapy Sessions" ? calculateTherapyTotal() : calculateStoreTotal()).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
               </div>
           </div>

        {/* ADDRESS SECTION */}
        <div className="user-profile-account-section">
          <div className="user-profile-account-box">
            <h3 className="user-profile-account-title">Account Details</h3>
            <p className="user-profile-detail-text" style={{ fontSize: '1rem', color: '#003049' }}>{effectiveUser?.name || effectiveUser?.displayName || "Guest User"}</p>
            <p className="user-profile-detail-text" style={{ fontSize: '1rem' }}>{effectiveUser?.email}</p>
          </div>
        </div>

        {/* <button onClick={onContinueBrowsing} style={styles.continueLink}>
          Continue Browsing
        </button> */}
        

        {/* SCHEDULE MODAL */}
        {schedulingItem && (
          <ScheduleModal
            item={schedulingItem}
            onClose={() => {
              setSchedulingItem(null);
              setReschedulingSessionNum(null);
            }}
            user={storedUser}
            rescheduleSessionNum={reschedulingSessionNum}
            onSuccess={() => {
              setSchedulingItem(null);
              setReschedulingSessionNum(null);
              alert(reschedulingSessionNum ? "Session Rescheduled Successfully!" : "Session Scheduled Successfully!");
              // Clear session cache so data refreshes
              clearUserSession();
              // Force refresh or let it happen on unmount
              window.location.reload();
            }}
          />
        )}

        {/* EDIT SESSIONS MODAL */}

        {showEditModalItem && (
          <EditSessionsModal
            isOpen={!!showEditModalItem}
            item={showEditModalItem}
            onClose={() => setShowEditModalItem(null)}
            onReschedule={(sessionNum) => {
              setReschedulingSessionNum(sessionNum);
              setSchedulingItem(showEditModalItem);
              setShowEditModalItem(null);
            }}
            onRenew={handleRenew}
            isSessionExpired={isSessionExpired}
            handleCancelBooking={handleCancelBooking}
            isWithinCancellationWindow={isWithinCancellationWindow}
            getTimeRemainingForCancellation={getTimeRemainingForCancellation}
            formatTimeRemaining={formatTimeRemaining}
            cancellingId={cancellingId}
          />
        )}

        {/* LIVE CHAT MODAL */}
        <AnimatePresence>
          {activeChatSessionId && (
            <div style={styles.chatOverlay}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                style={styles.chatModal}
              >
                <div style={styles.chatHeader}>
                  <h2 style={{ margin: 0, fontSize: '18px' }}></h2>
                  <button onClick={() => setActiveChatSessionId(null)} style={styles.chatCloseBtn}>✕</button>
                </div>
                <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                  <LiveChat
                    sessionId={activeChatSessionId}
                    currentUser={{ ...effectiveUser, uid: effectiveUser.uid, role: 'user' }}
                    onEnd={() => setActiveChatSessionId(null)}
                  />
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* QR CODE MODAL */}
        <AnimatePresence>
          {showQRId && (
            <div style={styles.chatOverlay}>
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                style={{ ...styles.chatModal, backgroundColor: '#FFF7F1', height: 'auto', padding: '24px', textAlign: 'center', maxWidth: '350px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setShowQRId(null)} style={styles.chatCloseBtn}>✕</button>
                </div>
                <img
                  src="/nabiwhatsappqrcode.jpeg"
                  alt="WhatsApp QR Code"
                  style={{ width: '200px', height: '200px', objectFit: 'contain', margin: '0 auto 20px' }}
                />
                <h3 style={{ margin: '0 0 10px', color: '#003049', fontSize: '18px' }}>Join WhatsApp Community</h3>
                <p style={{ fontSize: '14px', color: '#555', margin: 0, lineHeight: '1.5' }}>
                  Chat support is no longer available for this package. For any queries or guidelines scan QR code and join our WhatsApp community!
                </p>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* CLIENT ACTIVITY MODAL */}
        <ClientActivityModal
          isOpen={!!showActivity}
          booking={showActivity?.booking}
          sessionNumber={showActivity?.sessionNumber}
          onClose={() => setShowActivity(null)}
          userType="patient"
          initialActivities={sessionActivitiesMap[`${showActivity?.booking?.id}_${showActivity?.sessionNumber}`] || []}
        />

        {/* SESSION REVIEW MODAL */}
        <AnimatePresence>
          {pendingReview && (
            <ReviewModal
              isOpen={!!pendingReview}
              booking={pendingReview.booking}
              sessionNumber={pendingReview.sessionNumber}
              onSubmit={handleReviewSubmit}
            />
          )}
        </AnimatePresence>

        {/* Event Review Modal */}
        <AnimatePresence>
          {reviewingEvent && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="user-profile-modal-backdrop"
                onClick={() => setReviewingEvent(null)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 9999
                }}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="user-profile-modal-content"
                style={{ 
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '400px',
                  maxWidth: '100%',
                  backgroundColor: '#FFF7F1',
                  boxShadow: '-5px 0 15px rgba(0,0,0,0.1)',
                  zIndex: 10000,
                  padding: '40px 30px',
                  overflowY: 'auto',
                  borderTopLeftRadius: '20px',
                  borderBottomLeftRadius: '20px'
                }}
              >
                <button className="user-profile-modal-close" onClick={() => setReviewingEvent(null)} style={{ position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
                <h2 style={{ fontSize: '24px', color: '#003049', margin: '0 0 10px 0' }}>Review Event</h2>
                <p style={{ margin: '0 0 30px 0', fontSize: '15px', color: '#666' }}>{reviewingEvent.title}</p>
                
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold', color: '#333' }}>Rating</label>
                    <div style={{ display: 'flex', gap: '15px' }}>
                        {[
                            { val: 1, emoji: '😞' },
                            { val: 2, emoji: '😕' },
                            { val: 3, emoji: '😐' },
                            { val: 4, emoji: '😊' },
                            { val: 5, emoji: '🤩' }
                        ].map(item => (
                            <span 
                                key={item.val} 
                                onClick={() => setReviewForm({ ...reviewForm, rating: item.val })}
                                style={{ 
                                    cursor: 'pointer', 
                                    fontSize: '32px', 
                                    transition: 'all 0.2s ease',
                                    transform: item.val === reviewForm.rating ? 'scale(1.2)' : 'scale(1)',
                                    filter: item.val === reviewForm.rating ? 'none' : 'grayscale(100%)',
                                    opacity: item.val === reviewForm.rating ? 1 : 0.5
                                }}
                            >
                                {item.emoji}
                            </span>
                        ))}
                    </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>Your Review</label>
                    <textarea 
                        value={reviewForm.text}
                        onChange={(e) => setReviewForm({ ...reviewForm, text: e.target.value })}
                        placeholder="Share your experience..."
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ccc', minHeight: '100px', fontFamily: 'inherit' }}
                    />
                </div>

                <button 
                    onClick={async () => {
                        if (!reviewingEvent || !reviewForm.text.trim()) return;
                        try {
                          const reviewData = {
                            eventId: reviewingEvent.dbId,
                            eventName: reviewingEvent.title,
                            userEmail: effectiveUser.email,
                            userName: effectiveUser.displayName || effectiveUser.name || "Customer",
                            rating: reviewForm.rating,
                            reviewText: reviewForm.text,
                            status: "pending",
                            createdAt: serverTimestamp(),
                            eventDate: reviewingEvent.date || "Unknown"
                          };
                          await addDoc(collection(db, "event_reviews"), reviewData);
                          
                          setUserReviews(prev => ({
                              ...prev,
                              [reviewingEvent.dbId]: { ...reviewData, status: "pending" }
                          }));
                          setReviewingEvent(null);
                          setReviewForm({ rating: 5, text: "" });
                          alert("Review submitted! Waiting for admin approval.");
                        } catch (err) {
                          console.error("Error submitting review:", err);
                          alert("Failed to submit review.");
                        }
                    }}
                    style={{ width: '100%', padding: '12px', background: '#f29053', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    Submit Review
                </button>
              </motion.div>
            </>
          )}
        </AnimatePresence>

      </motion.div>
    </div>
  );
}

const styles = {
  chatInlineBtn: {
    backgroundColor: '#003049',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px'
  },
  chatOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  chatModal: {
    backgroundColor: '#FFF',
    borderRadius: '24px',
    width: '95%',
    maxWidth: '450px',
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  chatHeader: {
    padding: '16px 20px',
    backgroundColor: '#FFF7F1',
    borderBottom: '1px solid #F1E0D0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  chatCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#888'
  },
  activityBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'linear-gradient(135deg, #f07167  0%, #dba555  100%)',
    color: 'white',
    border: 'none',
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '10px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 4px 8px rgba(42, 157, 143, 0.15)',
    transition: 'all 0.3s ease',
    width: 'auto',
    justifyContent: 'center',
    marginTop: '6px'
  },
  backdrop: { position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.3)", zIndex: 2000 },
  notesModal: { position: 'fixed', top: '20%', left: '5%', right: '5%', width: '90%', maxWidth: '400px', margin: '0 auto', backgroundColor: 'white', borderRadius: '24px', padding: '30px', zIndex: 3001, textAlign: 'center', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' },
  modalHeaderBasic: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
  backBtnSimple: { background: 'none', border: 'none', cursor: 'pointer' },
  notesMainTitle: { fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' },
  editOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000
  },
  editModal: {
    backgroundColor: '#FFF',
    borderRadius: '24px',
    width: '90%',
    maxWidth: '380px',
    padding: '40px 30px 30px',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  editCloseBtn: {
    position: 'absolute',
    left: '20px',
    top: '20px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#888'
  },
  editTitle: {
    fontFamily: "'Syne', sans-serif",
    color: '#003049',
    marginTop: '10px',
    marginBottom: '24px',
    fontSize: '22px',
    fontWeight: '700',
    textAlign: 'center'
  },
  editBtn: {
    width: '100%',
    padding: '16px',
    borderRadius: '16px',
    border: 'none',
    backgroundColor: '#FFB085',
    color: '#003049',
    fontWeight: '700',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '15px',
    transition: 'all 0.2s ease',
    fontFamily: "'Syne', sans-serif",
    textAlign: 'center'
  },
  editWarning: {
    color: '#D62828',
    fontSize: '12px',
    margin: '-8px 0 16px',
    textAlign: 'center',
    lineHeight: '1.4',
    fontWeight: '500',
    maxWidth: '90%'
  }
};

// --- EDIT SESSIONS MODAL ---
const EditSessionsModal = ({
  isOpen,
  item,
  onClose,
  onReschedule,
  onRenew,
  isSessionExpired,
  handleCancelBooking,
  isWithinCancellationWindow,
  getTimeRemainingForCancellation,
  formatTimeRemaining,
  cancellingId
}) => {
  const [showSelectSession, setShowSelectSession] = useState(false);
  const [showCancelAccordion, setShowCancelAccordion] = useState(false);

  if (!isOpen || !item) return null;

  const getRescheduleableSessions = () => {
    const list = [];
    for (let i = 1; i <= item.sessionCount; i++) {
      let sessionData = null;
      if (item[`session_${i}`]) {
        sessionData = item[`session_${i}`];
      } else if (i === 1) {
        sessionData = item;
      }

      const hasData = !!sessionData && sessionData.date && sessionData.date !== "TBD";
      const slotExpired = hasData ? isSessionExpired(sessionData.date, sessionData.time || sessionData.timeSlot) : false;
      const isCompleted = hasData ? (sessionData.isTaken || sessionData.isCompleted) : false;

      if (hasData && !isCompleted && !slotExpired && item.status !== "cancelled") {
        list.push({
          sessionNumber: i,
          date: sessionData.date,
          time: sessionData.time || sessionData.timeSlot
        });
      }
    }
    return list;
  };

  const rescheduleableSessions = getRescheduleableSessions();
  const isWithin7Days = (Date.now() - (item.createdAt || Date.now())) <= 7 * 24 * 60 * 60 * 1000;
  const rescheduleCount = item.rescheduleCount || 0;
  const isRescheduleAllowed = isWithin7Days && rescheduleCount < 1;

  const handleRescheduleClick = () => {
    // if (rescheduleableSessions.length === 0) {
    //   alert("You have no upcoming scheduled sessions to reschedule.");
    //   return;
    // }
    if (rescheduleableSessions.length === 1) {
      onReschedule(rescheduleableSessions[0].sessionNumber);
    } else {
      setShowSelectSession(true);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "TBD") return dateStr;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    if (isNaN(d.getTime())) return dateStr;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  return (
    <div style={styles.editOverlay}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{ ...styles.editModal, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <button onClick={onClose} style={styles.editCloseBtn}>✕</button>

        {!showSelectSession ? (
          <>
            <h2 style={styles.editTitle}>Edit your Sessions</h2>

            <button
              onClick={handleRescheduleClick}
              disabled={!isRescheduleAllowed || rescheduleableSessions.length === 0}
              style={{
                ...styles.editBtn,
                marginBottom: '5px',
                opacity: (!isRescheduleAllowed || rescheduleableSessions.length === 0) ? 0.6 : 1,
                cursor: (!isRescheduleAllowed || rescheduleableSessions.length === 0) ? 'not-allowed' : 'pointer'
              }}
            >
              Reschedule
            </button>
            <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', marginTop: '0', marginBottom: '15px' }}>
              1 reschedule only allowed within 7 days of booked slot
            </p>

            {/* {rescheduleableSessions.length === 0 && (
              <p style={styles.editWarning}>No upcoming scheduled sessions to reschedule.</p>
            )} */}
            {!isWithin7Days && rescheduleableSessions.length > 0 && (
              <p style={styles.editWarning}>Reschedule window expired.</p>
            )}
            {rescheduleCount >= 1 && rescheduleableSessions.length > 0 && (
              <p style={styles.editWarning}>This package has already been rescheduled once (limit 1 reschedule).</p>
            )}

            <button onClick={onRenew} style={styles.editBtn}>
              Renew Package
            </button>

            {/* Cancel Accordion Button */}
            {item.status !== "cancelled" && (
              <button
                onClick={() => setShowCancelAccordion(!showCancelAccordion)}
                style={{
                  ...styles.editBtn,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  marginBottom: '15px'
                }}
              >
                <span style={{ flex: 1, textAlign: 'center', marginLeft: '12px' }}>Cancel & Get Refund</span>
                <span style={{
                  transform: showCancelAccordion ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  display: 'inline-block',
                  fontWeight: 'bold',
                  fontSize: '12px'
                }}>
                  ▼
                </span>
              </button>
            )}

            {/* Accordion Content */}
            {showCancelAccordion && item.status !== "cancelled" && (
              <div style={{
                width: '100%',
                backgroundColor: '#FFF5F5',
                borderRadius: '16px',
                padding: '15px',
                marginBottom: '15px',
                border: '1px solid #FFE3E3',
                boxSizing: 'border-box'
              }}>
                {isWithinCancellationWindow(item.createdAt) ? (
                  <div>
                    <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#D62828', fontWeight: '600', textAlign: 'center' }}>
                      ⏰ Available for: {formatTimeRemaining(getTimeRemainingForCancellation(item.createdAt))}
                    </p>
                    <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#555', textAlign: 'center', lineHeight: '1.4' }}>
                      Full refund of ₹{item.price} will be initiated.
                    </p>
                    <button
                      onClick={() => {
                        onClose();
                        handleCancelBooking(item);
                      }}
                      disabled={cancellingId === item.id}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '12px',
                        border: 'none',
                        backgroundColor: '#D62828',
                        color: '#FFF',
                        fontWeight: '700',
                        cursor: cancellingId === item.id ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        textAlign: 'center'
                      }}
                    >
                      {cancellingId === item.id ? "Processing..." : "✓ Confirm Cancel & Refund"}
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#D62828', fontWeight: 'bold' }}>
                      ❌ Window expired
                    </p>
                    <p style={{ margin: '0', fontSize: '11px', color: '#777', lineHeight: '1.4' }}>
                      Refunds are only available within 6 hours of booking.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <h2 style={styles.editTitle}>Select Session</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxHeight: '200px', overflowY: 'auto', marginBottom: '20px' }}>
              {rescheduleableSessions.map((sess) => (
                <button
                  key={sess.sessionNumber}
                  onClick={() => onReschedule(sess.sessionNumber)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    border: '1px solid #FFB085',
                    backgroundColor: '#FFF7F1',
                    color: '#003049',
                    fontWeight: '600',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: "'Syne', sans-serif"
                  }}
                >
                  Session {sess.sessionNumber}: {formatDate(sess.date)} ({sess.time})
                </button>
              ))}
            </div>
            <button onClick={() => setShowSelectSession(false)} style={{ ...styles.editBtn, backgroundColor: '#DDD', color: '#333' }}>
              Back
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

// --- REVIEW MODAL COMPONENT ---
const ReviewModal = ({ isOpen, booking, sessionNumber, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const emojis = ["😠", "🙁", "😐", "🙂", "🤩"];
  const labels = ["Poor", "Fair", "Good", "Very Good", "Excellent"];

  if (!isOpen) return null;

  return (
    <div style={styles.chatOverlay}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        style={{ ...styles.chatModal, height: 'auto', padding: '30px', maxWidth: '400px', borderRadius: '32px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          {/* <div style={{ fontSize: '40px', marginBottom: '10px' }}>✨</div> */}
          <h2 style={{ fontFamily: "'Syne', sans-serif",  color: '#666',margin: '0 0 8px', fontSize: '16px', fontWeight: '500' }}>The session has ended. Please provide your valuable feedback.</h2>
          <p style={{color: '#003049', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
           Hope you found the help with our therapist <strong>{booking.therapist}</strong>. Glad you took the step to have a conversation Let us know how you feel about it.
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px', padding: '0 10px' }}>
          {emojis.map((emoji, i) => (
            <button
              key={i}
              onClick={() => setRating(i + 1)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                padding: '5px',
                borderRadius: '12px',
                backgroundColor: rating === i + 1 ? '#FFF5F5' : 'transparent',
                transform: rating === i + 1 ? 'scale(1.15)' : 'scale(1)',
                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
              }}
            >
              <span style={{ fontSize: '32px', filter: rating === i + 1 ? 'grayscale(0)' : 'grayscale(0.5)', opacity: rating === i + 1 ? 1 : 0.6 }}>{emoji}</span>
              <span style={{ fontSize: '9px', fontWeight: '800', color: rating === i + 1 ? '#F07167' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{labels[i]}</span>
            </button>
          ))}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', marginBottom: '10px', color: '#003049', letterSpacing: '0.5px' }}>
            Any words to share?
          </label>
          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="It'll be helpful to our Therapists!"
            style={{
              width: '100%',
              height: '110px',
              padding: '15px',
              borderRadius: '16px',
              border: '1.5px solid #F1F2F6',
              fontSize: '14px',
              resize: 'none',
              outline: 'none',
              boxSizing: 'border-box',
              backgroundColor: '#F8FAFC',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => onSubmit({ rating, feedback, sessionNumber, bookingId: booking.id })}
            style={{
              flex: 1,
              padding: '16px',
              borderRadius: '16px',
              border: 'none',
              background: 'linear-gradient(135deg, #003049 0%, #00507A 100%)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '16px',
              boxShadow: '0 10px 20px rgba(0, 48, 73, 0.15)',
              transition: 'all 0.2s ease'
            }}
          >
            Submit feedback
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// --- ICONS ---
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

// --- SUB_COMPONENT for Scheduling ---
const ScheduleModal = ({ item, onClose, user, onSuccess, rescheduleSessionNum = null }) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null); // Store full slot object
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState(false);
  const [therapistSchedule, setTherapistSchedule] = useState(null);


  console.log(item, "itemshfbn");

  useEffect(() => {
    const fetchSchedule = async () => {
      if (!item.therapistId && !item.therapistEmail) return;
      try {
        const allSnap = await getDocs(collection(db, "therapist_availability"));
        const allDocs = allSnap.docs.map(d => ({ docId: d.id, ...d.data() }));

        const matchedDocs = allDocs.filter(d => {
          const isIdMatch = d.docId === item.therapistId;
          const tEmail = item.therapistEmail?.toLowerCase().trim();
          const dEmail = d.email?.toLowerCase().trim();
          const tName = item.therapist?.toLowerCase().trim();
          const dName = d.name?.toLowerCase().trim();
          return isIdMatch || (tEmail && dEmail === tEmail) || (tName && dName === tName);
        });
        console.log(matchedDocs, "matchedDocs")

        matchedDocs.sort((a, b) => {
          const aHas = a.monthlySlots ? 1 : 0;
          const bHas = b.monthlySlots ? 1 : 0;
          return bHas - aHas;
        });

        if (matchedDocs.length > 0) {
          setTherapistSchedule(matchedDocs[0]);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchSchedule();
  }, [item]);

  const getSlotStatus = (startTime, selectedDateStr) => {
    if (!startTime || !selectedDateStr) return 'available';

    const now = new Date();
    const selectedDate = new Date(selectedDateStr);

    // Only check if the selected date is TODAY
    const [year, month, day] = selectedDateStr.split('-');
    const isToday = now.getFullYear() === parseInt(year) &&
      (now.getMonth() + 1) === parseInt(month) &&
      now.getDate() === parseInt(day);

    if (isToday) {
      const match = startTime.match(/(\d+):(\d+)\s*(AM|PM|am|pm)?/);
      if (match) {
        let slotHours = parseInt(match[1], 10);
        const slotMinutes = parseInt(match[2], 10);
        const modifier = match[3];

        if (modifier) {
          if (modifier.toUpperCase() === 'PM' && slotHours < 12) slotHours += 12;
          if (modifier.toUpperCase() === 'AM' && slotHours === 12) slotHours = 0;
        }

        const slotDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), slotHours, slotMinutes, 0, 0);
        const bufferTime = 7 * 60 * 60 * 1000; // 7 hours

        if (slotDateTime < now) {
          return 'passed';
        } else if (slotDateTime.getTime() - now.getTime() < bufferTime) {
          return 'soon';
        }
      }
    }
    return 'available';
  };

  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate || !therapistSchedule) return;
      setLoadingSlots(true);
      try {
        const daySlotsData = therapistSchedule.monthlySlots?.[selectedDate] || [];

        const bookingsRef = collection(db, "bookings");
        const q = query(bookingsRef, where("therapistId", "==", therapistSchedule.docId));
        const snap = await getDocs(q);
        const booked = [];
        snap.docs.forEach(doc => {
          const data = doc.data();
          if (data.status === "cancelled") return;

          // Check root date (Session 1)
          if (data.date === selectedDate && data.timeSlot) {
            booked.push(data.timeSlot);
          }
          if (data.session_1 && data.session_1.date === selectedDate && data.session_1.time) {
            booked.push(data.session_1.time);
          }

          // Check subsequent sessions (2, 3, 4)
          for (let i = 2; i <= 4; i++) {
            const sess = data[`session_${i}`];
            if (sess && sess.date === selectedDate && sess.time) {
              booked.push(sess.time);
            }
          }
        });

        const processedSlots = daySlotsData.map(slot => {
          const formattedTimeSlot = `${slot.from} - ${slot.to}`;
          const isBooked = booked.includes(formattedTimeSlot);
          const status = getSlotStatus(slot.from, selectedDate);

          return { ...slot, isBooked, status };
        }).filter(slot => {
          return !slot.isBooked && slot.status !== 'passed';
        });

        setAvailableSlots(processedSlots);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate, therapistSchedule]);



  const handleBook = async () => {
    setBooking(true);
    try {
      // Update the existing parent document organically
      const parentRef = doc(db, "bookings", item.id);

      if (rescheduleSessionNum) {
        // Rescheduling an existing session
        const updatePayload = {
          [`session_${rescheduleSessionNum}.date`]: selectedDate,
          [`session_${rescheduleSessionNum}.time`]: selectedTime,
          [`session_${rescheduleSessionNum}.meetLink`]: "",
          [`session_${rescheduleSessionNum}.googleEventId`]: "",
          [`session_${rescheduleSessionNum}.emailSent`]: false,
          rescheduleCount: increment(1)
        };

        if (rescheduleSessionNum === 1) {
          updatePayload.date = selectedDate;
          updatePayload.timeSlot = selectedTime;
          updatePayload.meetLink = "";
          updatePayload.googleEventId = "";
          updatePayload.emailSent = false;
        }

        await updateDoc(parentRef, updatePayload);
      } else {
        // Original scheduling logic (scheduling a new slot)
        const sessionNum = item.sessionsUsed + 1;
        const sessionKey = `session_${sessionNum}`;

        await updateDoc(parentRef, {
          sessionsUsed: increment(1),
          [sessionKey]: {
            date: selectedDate,
            time: selectedTime,
            isTaken: false
          }
        });
      }

      onSuccess();
    } catch (err) {
      console.error(err);
      alert("Error scheduling session.");
      setBooking(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
      <div style={{ backgroundColor: '#fff', padding: '7px', borderRadius: '16px', width: '90%', maxWidth: '500px' }}>
        <h2 style={{ marginTop: 0 }}>{rescheduleSessionNum ? `Reschedule Session ${rescheduleSessionNum}` : "Schedule Next Session"}</h2>
        <p style={{ fontSize: '0.9rem', color: '#555' }}>Therapist: {item.therapist}</p>

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Date</label>
          <DatePicker
            selected={selectedDate ? new Date(selectedDate) : null}
            onChange={(date) => {
              if (date) {
                const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                setSelectedDate(ds);
                setSelectedTime("");
              }
            }}
            minDate={new Date()}
            dateFormat="yyyy-MM-dd"
            customInput={<input style={{ padding: '10px', width: '100%', borderRadius: '8px', border: '1px solid #ccc' }} />}
          />
        </div>

        <div style={{ marginTop: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Available Time Slots</label>
          {loadingSlots ? <p>Loading...</p> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {availableSlots.length > 0 ? availableSlots.map((slot, idx) => {
                const slotTime = `${slot.from}`;
                const isSelected = selectedTime === slotTime;
                const isSoon = slot.status === 'soon';

                return (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button
                      onClick={() => !isSoon && setSelectedTime(slotTime)}
                      style={{
                        padding: '8px 12px', border: '1px solid #003049', borderRadius: '8px',
                        backgroundColor: isSelected ? '#003049' : '#fff',
                        color: isSelected ? '#fff' : '#003049',
                        cursor: isSoon ? 'not-allowed' : 'pointer',
                        opacity: isSoon ? 0.6 : 1
                      }}
                      disabled={isSoon}
                    >
                      {slotTime}
                    </button>
                    {isSoon && (
                      <span style={{ fontSize: '10px', color: '#F07167', marginTop: '4px', fontWeight: 'bold' }}>
                        Booking closed
                      </span>
                    )}
                  </div>
                );
              }) : (
                <p style={{ fontSize: '0.9rem', color: '#F07167' }}>{selectedDate ? "No slots available." : "Select a date first."}</p>
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '30px' }}>
          <button onClick={onClose} style={{ padding: '10px 15px', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleBook} disabled={!selectedDate || !selectedTime || booking} style={{
            padding: '10px 20px', backgroundColor: '#003049', color: '#fff', borderRadius: '8px', border: 'none',
            opacity: (!selectedDate || !selectedTime || booking) ? 0.5 : 1, cursor: 'pointer'
          }}>
            {booking ? "Confirming..." : (rescheduleSessionNum ? "Confirm Reschedule" : "Confirm Slot")}
          </button>
        </div>
      </div>
    </div>
  );
};

// NEW MODAL: Client Activity Modal (Synced from TherapistDashboard)
const ClientActivityModal = ({ isOpen, booking, sessionNumber, onClose, userType = "patient", initialActivities = [] }) => {
  const [selectedSession, setSelectedSession] = useState(sessionNumber);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newActivity, setNewActivity] = useState({ title: "", category: "Mindfulness", description: "" });

  useEffect(() => {
    if (sessionNumber) {
      setSelectedSession(sessionNumber);
    }
  }, [sessionNumber]);

  useEffect(() => {
    if (isOpen && booking?.id) {
      // ALWAYS sync with latest data from real-time listener if it exists
      const currentActivities = initialActivities.length > 0 ? initialActivities : [];
      if (currentActivities.length > 0 || !loading) {
        setActivities(currentActivities);
      }

      // Only fetch if we don't have data in the map yet
      if (currentActivities.length === 0) {
        fetchActivities();
      }
    }
  }, [isOpen, booking?.id, selectedSession, initialActivities]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      // NEW: Fetch from dedicated session_activities collection
      const activityDocId = `${booking.id}_session_${selectedSession}`;
      const activityDoc = await getDoc(doc(db, "session_activities", activityDocId));

      if (activityDoc.exists()) {
        setActivities(activityDoc.data().activities || []);
      } else {
        // Fallback to old location in bookings (for migration/compatibility)
        const bookingDoc = await getDoc(doc(db, "bookings", booking.id));
        if (bookingDoc.exists()) {
          const data = bookingDoc.data();
          const oldActivities = data.activities?.[`session_${selectedSession}`] || [];
          setActivities(oldActivities);

          // Optionally migrate to new collection if therapist adds/toggles
        } else {
          setActivities([]);
        }
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async () => {
    if (userType === "patient") return;
    if (!newActivity.title.trim()) return;

    const activityToAdd = {
      id: Date.now().toString(),
      title: newActivity.title,
      category: newActivity.category,
      description: newActivity.description,
      completed: false,
      createdAt: new Date().toISOString(),
      suggestedBy: "therapist",
      icon: newActivity.category === "Mindfulness" ? "🧘" :
        newActivity.category === "Journaling" ? "✍️" :
          newActivity.category === "Physical" ? "🚶" : "📞"
    };

    try {
      const activityDocId = `${booking.id}_session_${selectedSession}`;
      const activityRef = doc(db, "session_activities", activityDocId);
      const updatedActivities = [...activities, activityToAdd];

      await setDoc(activityRef, {
        activities: updatedActivities,
        bookingId: booking.id,
        sessionNumber: selectedSession,
        therapistId: booking.therapistId || "",
        therapistName: booking.therapistName || "",
        patientId: booking.userId || "",
        patientName: booking.userName || booking.fullName || "",
        updatedAt: serverTimestamp()
      }, { merge: true });

      setActivities(updatedActivities);
      setNewActivity({ title: "", category: "Mindfulness", description: "" });
      setShowAddForm(false);
    } catch (error) {
      console.error("Error adding activity:", error);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (userType === "patient") return;
    try {
      const activityDocId = `${booking.id}_session_${selectedSession}`;
      const activityRef = doc(db, "session_activities", activityDocId);
      const updatedActivities = activities.filter(a => a.id !== activityId);

      await updateDoc(activityRef, {
        activities: updatedActivities,
        updatedAt: serverTimestamp()
      });

      setActivities(updatedActivities);
    } catch (error) {
      console.error("Error deleting activity:", error);
    }
  };

  const handleToggleActivity = async (activityId) => {
    try {
      const activityDocId = `${booking.id}_session_${selectedSession}`;
      const activityRef = doc(db, "session_activities", activityDocId);
      const updatedActivities = activities.map(a =>
        a.id === activityId ? { ...a, completed: !a.completed, completedAt: !a.completed ? new Date().toISOString() : null } : a
      );

      await setDoc(activityRef, {
        activities: updatedActivities,
        bookingId: booking.id,
        sessionNumber: selectedSession,
        therapistId: booking.therapistId || "",
        therapistName: booking.therapistName || "",
        patientId: booking.userId || "",
        patientName: booking.userName || booking.fullName || "",
        updatedAt: serverTimestamp()
      }, { merge: true });

      setActivities(updatedActivities);
    } catch (error) {
      console.error("Error toggling activity:", error);
    }
  };

  if (!isOpen || !booking) return null;

  const stats = {
    completed: activities.filter(a => a.completed).length,
    total: activities.length,
    rate: activities.length > 0 ? Math.round((activities.filter(a => a.completed).length / activities.length) * 100) : 0
  };

  return (
    <AnimatePresence>
      {isOpen && booking && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.backdrop}
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="activity-modal"
          >
            <div className="activity-modal-header">
              <div style={{ ...styles.modalHeaderBasic, marginBottom: '10px' }}>
                <button onClick={onClose} className="back-btn-simple">
                  <ArrowLeftIcon />
                </button>
                <h2 style={{ ...styles.notesMainTitle, margin: 0 }}>Therapy Activities</h2>
                <div style={{ width: '28px' }}></div>
              </div>
            </div>

            <div className="activity-modal-body">
              {/* Session Tabs */}
              <div className="modal-session-tabs">
                {[...Array(parseInt(booking.sessionCount) || 1)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedSession(i + 1)}
                    className={`modal-tab-btn ${selectedSession === i + 1 ? 'active' : ''}`}
                  >
                    Session {i + 1}
                  </button>
                ))}
              </div>

              <div className="modal-progress-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Your Progress</h3>
                  <span style={{ fontSize: '12px', color: '#C57B57', fontWeight: 'bold' }}>Session {selectedSession}</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#003049' }}>{stats.completed}/{stats.total}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>Done</div>
                  </div>
                  <div style={{ flex: 2 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{stats.rate}%</span>
                    </div>
                    <div style={{ width: '100%', height: '6px', backgroundColor: '#E0E0E0', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${stats.rate}%`,
                        height: '100%',
                        backgroundColor: '#2A9D8F',
                        transition: 'width 0.5s ease-out'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Activities</h3>
                {userType === "therapist" && !showAddForm && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    style={{
                      backgroundColor: '#F07167',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    <span>+ Add Activity</span>
                  </button>
                )}
              </div>

              <div className="activity-list">
                {loading ? (
                  <p style={{ textAlign: 'center', color: '#999', fontSize: '13px' }}>Loading activities...</p>
                ) : activities.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#F9F9F9', borderRadius: '12px', color: '#999' }}>
                    <p style={{ fontSize: '13px', margin: 0 }}>No activities assigned for this session yet.</p>
                  </div>
                ) : (
                  activities.map(activity => {
                    const getTagColor = (cat) => {
                      switch (cat) {
                        case 'Mindfulness': return { bg: '#F3E5F5', text: '#9C27B0' };
                        case 'Physical': return { bg: '#E8F5E9', text: '#4CAF50' };
                        case 'Journaling': return { bg: '#E3F2FD', text: '#2196F3' };
                        case 'Social': return { bg: '#FCE4EC', text: '#E91E63' };
                        default: return { bg: '#F5F5F5', text: '#757575' };
                      }
                    };
                    const colors = getTagColor(activity.category);

                    return (
                      <div key={activity.id} className="activity-item">
                        <div style={{ paddingTop: '2px' }}>
                          <input
                            type="checkbox"
                            checked={activity.completed}
                            onChange={() => handleToggleActivity(activity.id)}
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                              accentColor: '#2A9D8F'
                            }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h4 style={{
                              margin: '0 0 4px 0',
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#333',
                              textDecoration: activity.completed ? 'line-through' : 'none'
                            }}>{activity.title}</h4>
                            {userType === "therapist" && (
                              <button
                                onClick={() => handleDeleteActivity(activity.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#BBB', fontSize: '14px' }}
                              >🗑️</button>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '4px', alignItems: 'center' }}>
                            <span style={{
                              fontSize: '9px',
                              backgroundColor: colors.bg,
                              color: colors.text,
                              padding: '1px 6px',
                              borderRadius: '4px',
                              fontWeight: '600'
                            }}>{activity.category}</span>
                          </div>
                          <p style={{ margin: 0, fontSize: '11px', color: '#666', lineHeight: '1.4' }}>{activity.description}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
